/**
 * Mi cuenta: perfil y datos del usuario.
 */
import React, { useRef, useState } from 'react';
import { storage } from '../firebaseConfig';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import EditIcon from '../assets/icons/edit.svg';
import avatar1_local from '../assets/avatar/avatar_f.png';
import avatar2_local from '../assets/avatar/avatar_m.png';
import avatar3_local from '../assets/avatar/avatar_n.png';
import avatar4_local from '../assets/avatar/avatar_b.png';
import imageCompression from 'browser-image-compression';
import { useAuth } from '../AuthContext';
import { isNarrowViewport } from '../useMediaQuery';
import { useNavigation } from '../NavigationContext';
import { ink, studio, RoleBadge, Field, FULL_ROW } from './admin/studioUi';

const localAvatars = [avatar1_local, avatar2_local, avatar3_local, avatar4_local];
const publicAvatarUrls = [
    'https://firebasestorage.googleapis.com/v0/b/cm-bi-f1.firebasestorage.app/o/avatars%2Favatar_f.png?alt=media',
    'https://firebasestorage.googleapis.com/v0/b/cm-bi-f1.firebasestorage.app/o/avatars%2Favatar_m.png?alt=media',
    'https://firebasestorage.googleapis.com/v0/b/cm-bi-f1.firebasestorage.app/o/avatars%2Favatar_n.png?alt=media',
    'https://firebasestorage.googleapis.com/v0/b/cm-bi-f1.firebasestorage.app/o/avatars%2Favatar_b.png?alt=media'
];

const formatLastLogin = (timestamp) => {
    if (!timestamp) return 'Nunca';
    const date = timestamp.toDate
        ? timestamp.toDate()
        : timestamp._seconds
            ? new Date(timestamp._seconds * 1000)
            : new Date(timestamp);
    if (Number.isNaN(date.getTime())) return 'Nunca';
    return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
};

const AccountPanel = () => {
    const { user, userData, onUserDataUpdate, fetchWithAuth, withBusy } = useAuth();
    const { setView } = useNavigation();
    const API_URL_INTERNAL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [verPassword, setVerPassword] = useState(false);
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [showPhotoOptions, setShowPhotoOptions] = useState(false);
    const [photoUrlInput, setPhotoUrlInput] = useState('');
    const [uploading, setUploading] = useState(false);
    const [avatarSelectionMode, setAvatarSelectionMode] = useState('select');
    const fileInputRef = useRef(null);

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            return alert('Las contraseñas no coinciden.');
        }
        if (!password) {
            return alert('La nueva contraseña no puede estar vacía.');
        }

        try {
            await withBusy(async () => {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/user/update-password/${user.uid}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password })
            });

            if (res.ok) {
                alert('Contraseña actualizada con éxito.');
                setPassword('');
                setConfirmPassword('');
                setVerPassword(false);
                setShowPasswordForm(false);
            } else {
                const data = await res.json().catch(() => ({ error: 'Error desconocido al actualizar la contraseña.' }));
                throw new Error(data.error);
            }
            }, 'Actualizando contraseña…');
        } catch (error) {
            alert(`Error de conexión: ${error.message}`);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 1 * 1024 * 1024) {
            alert('El archivo es demasiado grande. El peso de la imagen no debe superar 1MB.');
            e.target.value = null;
            return;
        }

        setUploading(true);

        try {
            await withBusy(async () => {
            const compressedFile = await imageCompression(file, {
                maxSizeMB: 0.1,
                maxWidthOrHeight: 1024,
                useWebWorker: true,
            });
            const fileExtension = file.name.split('.').pop();
            const imageRef = storageRef(storage, `perfiles/${user.uid}/profile.${fileExtension}`);
            const snapshot = await uploadBytes(imageRef, compressedFile);
            const photoURL = await getDownloadURL(snapshot.ref);

            const res = await fetchWithAuth(`${API_URL_INTERNAL}/user/update-photo/${user.uid}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ photoURL })
            });

            if (res.ok) {
                onUserDataUpdate({ photoURL });
                alert('Foto de perfil actualizada.');
                setShowPhotoOptions(false);
            } else {
                const data = await res.json().catch(() => ({ error: 'Error desconocido al guardar la foto.' }));
                alert(`Error al guardar la foto: ${data.error}`);
            }
            }, 'Actualizando foto…');
        } catch (error) {
            alert(`Error al procesar la foto: ${error.message}`);
        } finally {
            setUploading(false);
            e.target.value = null;
        }
    };

    const handleUpdatePhotoUrl = async (photoURL) => {
        if (!photoURL) {
            return alert('Por favor, ingresa una URL de imagen.');
        }

        setUploading(true);
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/user/update-photo/${user.uid}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ photoURL })
            });

            if (res.ok) {
                onUserDataUpdate({ photoURL });
                alert('Foto de perfil actualizada.');
                setShowPhotoOptions(false);
                setPhotoUrlInput('');
            } else {
                const data = await res.json().catch(() => ({ error: 'Error desconocido al guardar la foto.' }));
                alert(`Error al guardar la foto: ${data.error}`);
            }
        } catch (error) {
            alert(`Error de conexión: ${error.message}`);
        } finally {
            setUploading(false);
        }
    };

    if (!userData) {
        return <div style={acc.page}><p style={studio.empty}>Cargando datos de la cuenta…</p></div>;
    }

    const email = userData.correo || userData.email || user?.email || '';
    const facts = [
        { label: 'Empresa', value: userData.empresa || '—' },
        { label: 'Área', value: userData.area || '—' },
        { label: 'Último acceso', value: formatLastLogin(userData.lastLogin) },
    ];

    return (
        <div style={acc.page}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Perfil</p>
                    <h2 style={studio.title}>Mi cuenta</h2>
                    <p style={studio.subtitle}>Tu identidad en el portal y las opciones de seguridad.</p>
                </div>
                <button type="button" onClick={() => setView('user')} style={studio.ghostBtn}>‹ Volver a reportes</button>
            </div>

            <div style={studio.card}>
                <div style={acc.hero}>
                    <div style={acc.avatarWrap}>
                        <img
                            src={userData.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.nombre || '?')}&background=5E35B1&color=fff&size=160`}
                            alt=""
                            style={acc.avatar}
                            referrerPolicy="no-referrer"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPhotoOptions((open) => !open)}
                            style={acc.editPhoto}
                            disabled={uploading}
                            title="Cambiar foto"
                        >
                            {uploading ? '…' : <img src={EditIcon} alt="" style={{ width: 14, height: 14 }} />}
                        </button>
                        <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} style={{ display: 'none' }} accept="image/png, image/jpeg" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <h3 style={acc.displayName}>{userData.nombre}</h3>
                        <p style={acc.email}>{email}</p>
                        <div style={{ marginTop: 10 }}>
                            <RoleBadge roleId={userData.rol} />
                        </div>
                    </div>
                </div>

                <div style={acc.facts}>
                    {facts.map((fact) => (
                        <div key={fact.label} style={acc.fact}>
                            <span style={studio.statLabel}>{fact.label}</span>
                            <span style={acc.factValue}>{fact.value}</span>
                        </div>
                    ))}
                </div>

                {showPhotoOptions && (
                    <div style={{ ...studio.formPanel, marginTop: 18, marginBottom: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                            <div>
                                <h4 style={{ ...studio.formTitle, margin: 0 }}>Foto de perfil</h4>
                                <p style={{ ...studio.hint, marginTop: 4 }}>Elige un avatar, pega una URL o sube un archivo (máx. 1 MB).</p>
                            </div>
                            <div style={studio.scopeBar}>
                                {[
                                    { id: 'select', label: 'Avatar' },
                                    { id: 'url', label: 'URL' },
                                    { id: 'upload', label: 'Subir' },
                                ].map((mode) => (
                                    <button
                                        key={mode.id}
                                        type="button"
                                        onClick={() => setAvatarSelectionMode(mode.id)}
                                        style={avatarSelectionMode === mode.id ? studio.scopeOn : studio.scopeOff}
                                    >
                                        {mode.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {avatarSelectionMode === 'select' && (
                            <div style={acc.avatarRow}>
                                {localAvatars.map((avatarSrc, index) => {
                                    const publicUrl = publicAvatarUrls[index];
                                    const selected = userData.photoURL === publicUrl;
                                    return (
                                        <button
                                            key={publicUrl}
                                            type="button"
                                            onClick={() => handleUpdatePhotoUrl(publicUrl)}
                                            style={{ ...acc.avatarPick, ...(selected ? acc.avatarPickOn : {}) }}
                                            disabled={uploading}
                                        >
                                            <img src={avatarSrc} alt="" style={acc.avatarPickImg} />
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {avatarSelectionMode === 'url' && (
                            <form
                                onSubmit={(e) => { e.preventDefault(); handleUpdatePhotoUrl(photoUrlInput); }}
                                style={{ display: 'flex', gap: 10, alignItems: 'center' }}
                            >
                                <input
                                    type="url"
                                    placeholder="Pega la URL de una imagen"
                                    value={photoUrlInput}
                                    onChange={(e) => setPhotoUrlInput(e.target.value)}
                                    style={{ ...studio.control, flex: 1 }}
                                />
                                <button type="submit" style={studio.primaryBtn} disabled={uploading}>
                                    {uploading ? 'Guardando…' : 'Guardar'}
                                </button>
                            </form>
                        )}

                        {avatarSelectionMode === 'upload' && (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                style={acc.uploadZone}
                                disabled={uploading}
                            >
                                {uploading ? 'Subiendo…' : 'Seleccionar archivo (PNG o JPG)'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div style={studio.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                    <div>
                        <p style={studio.kicker}>Seguridad</p>
                        <h3 style={{ ...studio.formTitle, margin: '6px 0 0' }}>Contraseña</h3>
                        <p style={{ ...studio.hint, marginTop: 6 }}>Cámbiala cuando quieras. Se pedirá en el próximo inicio de sesión.</p>
                    </div>
                    {!showPasswordForm && (
                        <button type="button" onClick={() => setShowPasswordForm(true)} style={studio.primaryBtn}>
                            Cambiar contraseña
                        </button>
                    )}
                </div>

                {showPasswordForm && (
                    <form onSubmit={handlePasswordUpdate} style={{ marginTop: 18, display: 'grid', gridTemplateColumns: acc.passwordCols, gap: 14 }}>
                        <Field label="Nueva contraseña">
                            <div style={{ position: 'relative' }}>
                                <input
                                    placeholder="Nueva contraseña"
                                    type={verPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    style={{ ...studio.control, width: '100%' }}
                                    required
                                />
                                <button type="button" onClick={() => setVerPassword(!verPassword)} style={acc.eyeBtn}>
                                    {verPassword ? 'Ocultar' : 'Ver'}
                                </button>
                            </div>
                        </Field>
                        <Field label="Confirmar">
                            <input
                                placeholder="Repite la contraseña"
                                type={verPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                style={studio.control}
                                required
                            />
                        </Field>
                        <div style={{ gridColumn: FULL_ROW, display: 'flex', gap: 10 }}>
                            <button type="submit" style={studio.primaryBtn}>Actualizar contraseña</button>
                            <button
                                type="button"
                                onClick={() => { setShowPasswordForm(false); setPassword(''); setConfirmPassword(''); }}
                                style={studio.ghostBtn}
                            >
                                Cancelar
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const acc = {
    passwordCols: '1fr 1fr',
    page: {
        ...studio.page,
        padding: '32px 36px 48px',
        maxWidth: 880,
        margin: '0 auto',
    },
    hero: {
        display: 'flex',
        alignItems: 'center',
        gap: 22,
        paddingBottom: 20,
        marginBottom: 18,
        borderBottom: `1px solid ${ink.line}`,
    },
    avatarWrap: { position: 'relative', flexShrink: 0 },
    avatar: {
        width: 96,
        height: 96,
        borderRadius: '50%',
        objectFit: 'cover',
        border: `3px solid ${ink.accentSoft}`,
        display: 'block',
        boxShadow: '0 8px 20px rgba(94, 53, 177, 0.12)',
    },
    editPhoto: {
        position: 'absolute',
        bottom: 2,
        right: 2,
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: `1px solid ${ink.lineStrong}`,
        backgroundColor: ink.surface,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        boxShadow: '0 2px 8px rgba(28, 29, 33, 0.12)',
    },
    displayName: {
        margin: 0,
        fontSize: '1.45rem',
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.03em',
        lineHeight: 1.2,
    },
    email: {
        margin: '6px 0 0',
        color: ink.muted,
        fontSize: '0.95rem',
    },
    facts: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 10,
    },
    fact: {
        ...studio.stat,
        minWidth: 0,
    },
    factValue: {
        fontSize: '0.95rem',
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.02em',
        lineHeight: 1.35,
        marginTop: 4,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    avatarRow: {
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
    },
    avatarPick: {
        appearance: 'none',
        borderWidth: 3,
        borderStyle: 'solid',
        borderColor: ink.line,
        backgroundColor: ink.wash,
        padding: 0,
        borderRadius: '50%',
        width: 64,
        height: 64,
        cursor: 'pointer',
        overflow: 'hidden',
    },
    avatarPickOn: {
        borderColor: ink.accent,
        boxShadow: '0 0 0 3px rgba(94, 53, 177, 0.16)',
    },
    avatarPickImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    uploadZone: {
        width: '100%',
        padding: '22px 16px',
        backgroundColor: ink.surface,
        color: ink.muted,
        border: `1px dashed ${ink.lineStrong}`,
        borderRadius: 14,
        cursor: 'pointer',
        fontWeight: 600,
        fontFamily: 'inherit',
        fontSize: '0.9rem',
    },
    eyeBtn: {
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '0.78rem',
        color: ink.muted,
        fontWeight: 700,
        fontFamily: 'inherit',
    },
};

/* Ajustes para pantallas angostas (se aplican al cargar el módulo). */
if (isNarrowViewport()) {
    acc.passwordCols = 'minmax(0, 1fr)';
    Object.assign(acc.page, { padding: '16px 12px 32px' });
    Object.assign(acc.hero, { gap: 14, flexWrap: 'wrap' });
    Object.assign(acc.avatar, { width: 72, height: 72 });
    Object.assign(acc.editPhoto, { width: 36, height: 36 });
    Object.assign(acc.displayName, { fontSize: '1.2rem', overflowWrap: 'anywhere' });
    Object.assign(acc.email, { overflowWrap: 'anywhere' });
    Object.assign(acc.facts, { gridTemplateColumns: 'minmax(0, 1fr)' });
    Object.assign(acc.eyeBtn, { minHeight: 36, padding: '0 6px' });
}

export default AccountPanel;
